import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '../../store'

import { PAGE_SIZE, arrayToObject } from './helpers'
import {
  fetchNotifications,
  markAllAsRead,
  updateNotification
} from '../../lib/api'
import { AddErrorAlert } from '../alert'

export { PAGE_SIZE } from './helpers'

type NotificationState = {
  loading: boolean
  loaded: boolean
  all: Record<string, APINotification>
  unread: Record<string, APINotification>
  after: string | null
}

const initialState: NotificationState = {
  loading: false,
  loaded: false,
  all: {},
  unread: {},
  after: null
}

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    startLoadAll: (state: NotificationState) => {
      state.loading = true
    },

    finishLoadAll: (
      state: NotificationState,
      action: PayloadAction<APINotification[]>
    ) => {
      state.loaded = true
      state.loading = false
      state.all = arrayToObject(action.payload)
    },

    finishLoadUnread: (
      state: NotificationState,
      action: PayloadAction<APINotification[]>
    ) => {
      state.unread = arrayToObject(action.payload)
    },

    push: (
      state: NotificationState,
      action: PayloadAction<APINotification>
    ) => {
      const notification = action.payload
      state.all[notification.id] = notification
      state.unread[notification.id] = notification
    },

    markRead: (
      state: NotificationState,
      action: PayloadAction<APINotification>
    ) => {
      const notification = action.payload
      state.all[notification.id] = notification
      delete state.unread[notification.id]
    },

    markAllRead: (state: NotificationState) => {
      state.unread = {}
      const date = new Date().toISOString()
      Object.values(state.all).forEach(notification => {
        if (!notification.read) {
          notification.read = date
        }
      })
    },

    reset: () => {
      return initialState
    },

    pushPage: (
      state: NotificationState,
      action: PayloadAction<APINotification[]>
    ) => {
      const newNotifications = arrayToObject(action.payload)
      state.all = { ...state.all, ...newNotifications }
    },

    setAfter: (
      state: NotificationState,
      action: PayloadAction<string | null>
    ) => {
      state.after = action.payload
    }
  }
})

export const { push } = notificationSlice.actions
const {
  finishLoadUnread,
  startLoadAll,
  setAfter,
  finishLoadAll,
  markRead: markReadAction,
  markAllRead: markAllReadAction,
  reset,
  pushPage
} = notificationSlice.actions

export const loadUnread = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await fetchNotifications(true)
      dispatch(finishLoadUnread(data))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const loadAll = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(startLoadAll())
    try {
      const { data, next } = await fetchNotifications(false, PAGE_SIZE)
      dispatch(setAfter(next))
      dispatch(finishLoadAll(data))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const markRead = (id: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { all, unread } = selectNotification(getState())
      const old = unread[id] ?? all[id]
      if (!old?.read) {
        const notification = await updateNotification(id, {
          read: new Date().toISOString()
        })
        dispatch(markReadAction(notification))
      }
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const markAllRead = () => {
  return async (dispatch: AppDispatch) => {
    try {
      await markAllAsRead()
      dispatch(markAllReadAction())
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const reload = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(reset())
    dispatch(loadUnread())
  }
}

export const getNextPage = (
  cb: (result: {
    data: APINotification[]
    extra: { next?: string | null }
  }) => void
) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { loaded, after } = selectNotification(getState())
    if (!loaded || !after) {
      cb({ data: [], extra: {} })
      return
    }

    try {
      const { data, next } = await fetchNotifications(false, PAGE_SIZE, after)
      dispatch(setAfter(next))
      dispatch(pushPage(data))
      cb({ data, extra: { next } })
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
      cb({ data: [], extra: { next: null } })
    }
  }
}

export const selectNotification = (state: RootState) => state.notification

export default notificationSlice.reducer
